import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  InputGroup,
  InputGroupText,
} from "reactstrap"
import { useSelector, useDispatch } from "react-redux"
import { createSelector } from "reselect"
import { Link } from "react-router-dom"
import withRouter from "components/Common/withRouter"
import * as Yup from "yup"
import { useFormik } from "formik"
import { clearError, clearMessage, clearSuccess, loginUser } from "../../store/actions"
import logo from "../../assets/images/light-logo-coldblock.png"
import { RESET_STAGE } from "store/auth/register/actionTypes"
import { RESET_STEP } from "store/auth/forgetpwd/actionTypes"
import PortalSupportNotice from "components/Common/PortalSupportNotice"

const Login = props => {
  document.title = "Signin - ColdBlock Technologies Inc."

  const dispatch = useDispatch()
  const [showPassword, setShowPassword] = useState(false)
  const { resetPassSuccess } = useSelector((state) => state.ForgetPassword || {})

  const validation = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().required("Please Enter Your Email"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: values => {
      dispatch(loginUser(values, props.router.navigate))
    },
  })

  const selectLoginState = state => state.Login
  const LoginProperties = createSelector(selectLoginState, login => ({
    error: login.error,
    loading: login.loading,
  }))

  const { error, loading } = useSelector(LoginProperties)

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError())
      }, 7000)
      return () => clearTimeout(timer)
    }
  }, [error, dispatch])

  useEffect(() => {
    if (resetPassSuccess) {
      dispatch(clearError());

      const timer = setTimeout(() => {
        dispatch(clearSuccess())
      }, 7000)

      return () => clearTimeout(timer)
    }
  }, [resetPassSuccess, dispatch])

  return (
    <React.Fragment>
      <div className="account-pages vh-100 d-flex justify-content-center align-items-center">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-primary-subtle">
                  <Row className="d-flex align-items-center">
                    <Col className="col-6">
                      <div className="text-primary p-4">
                        <h5 className="text-primary white-txt">
                          Welcome!
                        </h5>
                        <p className="white-txt">
                          Sign in to continue to
                          <span className="fw-bolder">
                            {" "}
                            ColdBlock Technologies Inc.{" "}
                          </span>
                        </p>
                      </div>
                    </Col>
                    <Link to="/" className="col-6 p-4">
                      <img src={logo} alt="" className="img-fluid" />
                    </Link>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div className="p-2">
                    <Form
                      className="form-horizontal"
                      onSubmit={validation.handleSubmit}
                    >
                      {resetPassSuccess && (
                        <Alert color="success">
                          Your password has been reset successfully. Please signin with your new password.
                        </Alert>
                      )}

                      {error ? <Alert color="danger">{error}</Alert> : null}

                      <div className="mb-3 mt-4">
                        <Label className="form-label">Email</Label>
                        <Input
                          name="email"
                          className="form-control"
                          placeholder="Please enter email"
                          type="email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation?.values.email || ""}
                          invalid={
                            validation.touched.email && validation.errors.email
                              ? true
                              : false
                          }
                        />
                        {validation.touched.email && validation.errors.email ? (
                          <FormFeedback type="invalid">
                            {validation.errors.email}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">Password</Label>

                        <InputGroup>
                          <Input
                            name="password"
                            value={validation.values.password || ""}
                            type={showPassword ? "text" : "password"}
                            placeholder="Please enter password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            autoComplete="current-password"
                            invalid={
                              validation.touched.password && validation.errors.password
                                ? true
                                : false
                            }
                          />

                          <InputGroupText
                            role="button"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            onClick={() => setShowPassword(prev => !prev)}
                            onMouseDown={e => e.preventDefault()}
                            className="bg-transparent"
                            style={{ cursor: "pointer" }}
                          >
                            <i className={showPassword ? "mdi mdi-eye-off" : "mdi mdi-eye"} />
                          </InputGroupText>
                        </InputGroup>

                        {validation.touched.password && validation.errors.password ? (
                          <FormFeedback type="invalid" style={{ display: "block" }}>
                            {validation.errors.password}
                          </FormFeedback>
                        ) : null}
                      </div>

                      {/* <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="customControlInline"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="customControlInline"
                        >
                          Remember me
                        </label>
                      </div> */}

                      <div className="mt-3 pt-2 d-grid">
                        <button
                          className="btn btn-primary btn-block primary-btn-bg"
                          type="submit"
                        >
                          {loading ? "Please wait..." : "Log In"}
                        </button>
                      </div>
                      {props.router.location.pathname === "/signin" &&
                        <div>
                          <div className="mt-4 text-center">
                            <Link
                              to="/reset-password"
                              onClick={() =>
                                dispatch({ type: RESET_STEP },
                                  dispatch(clearSuccess())
                                )}
                              className="text-muted"
                            >
                              <i className="mdi mdi-lock me-1"></i> Forgot your
                              password?
                            </Link>
                          </div>
                          <div className="mt-3 text-center">
                            <span className="text-muted me-2">New here?</span>
                            <Link
                              to="/signup"
                              onClick={() =>
                                dispatch({ type: RESET_STAGE },
                                  dispatch(clearMessage()),
                                  dispatch(clearError())
                                )}
                              className="btn btn-outline-primary btn-rounded px-4"
                              style={{ borderRadius: 50 }}
                            >
                              <i className="mdi mdi-account-plus me-1" /> Create an account
                            </Link>
                          </div>
                        </div>
                      }
                      <div className="mt-3 text-center">
                        <PortalSupportNotice />
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withRouter(Login)

Login.propTypes = {
  history: PropTypes.object,
}
